import React from "react";
import { chartPeriods } from "app/charts/types";
import { formatDate, formatTime } from "app/formatting/functions";

import "./style.scss";
import { ChartData } from "app/charts/BasicChart/BasicChart";

export interface TimeAxisProps {
    fenceStart: number;
    fenceEnd: number;
    period: chartPeriods;
    dataPoints: ChartData;
}

const getDifferenceInHours = (startTime: number, endTime: number): number => {
    const delta = endTime - startTime;
    return Math.floor(delta / (60 * 60 * 1000));
};

const getSizeByTime = (startTime: number, endTime: number, sizeStart: number, sizeEnd: number): number => {
    const begin = Math.max(startTime, sizeStart);
    const end = Math.min(endTime, sizeEnd);
    return (end - begin) / ((endTime - startTime) / 100);
};

export const getSizeByIndex = (data: ChartData, timeStart: number, timeEnd: number) => {
    const entries = data.filter(([time]) => time >= timeStart && time < timeEnd);
    return entries.length / (data.length / 100);
};

const stepHour = (d: Date) => {
    d.setTime(d.getTime() + 60 * 60 * 1000);
};

const stepWeek = (d: Date) => {
    d.setTime(d.getTime() + 7 * 24 * 60 * 60 * 1000);
};

const stepQuarter = (d: Date) => {
    d.setMonth(d.getMonth() + 3);
};

const formatQuarter = (d: Date): string => {
    if (d.getMonth() < 3) {
        return `Jan '${d
            .getFullYear()
            .toString()
            .substr(2)}`;
    }
    if (d.getMonth() < 6) {
        return `Apr '${d
            .getFullYear()
            .toString()
            .substr(2)}`;
    }
    if (d.getMonth() < 9) {
        return `Jul '${d
            .getFullYear()
            .toString()
            .substr(2)}`;
    }
    return `Okt '${d
        .getFullYear()
        .toString()
        .substr(2)}`;
};

export const snapToQuarter = (d: Date) => {
    d.setMonth(Math.floor(d.getMonth() / 3) * 3);
    d.setDate(1);
};

export const snapToWeek = (d: Date) => {
    switch (d.getDay()) {
        case 0:
            d.setDate(d.getDate() - 6);
            return;
        case 1:
            return;
        default:
            d.setDate(d.getDate() - (d.getDay() - 1));
    }
};

export const snapToYear = (d: Date) => {
    d.setMonth(0);
    d.setDate(1);
};

export const snapToDay = (d: Date) => {
    d.setHours(0);
    d.setMinutes(0);
    d.setSeconds(0);
    d.setMilliseconds(0);
};

export const formatYear = (d: Date) => d.getFullYear();

export const stepYear = (d: Date) => {
    d.setFullYear(d.getFullYear() + 1);
};

type LabelObjects = { label: string; size: number }[];

function getTimeOfEarliestDataPoint(dataPoints: ChartData): number {
    let earliestTime = Infinity;

    dataPoints.forEach(([timestamp]) => (earliestTime = Math.min(earliestTime, timestamp)));

    return earliestTime;
}

function getTimeOfLatestDataPoint(dataPoints: ChartData): number {
    let latestTime = 0;

    dataPoints.forEach(([timestamp]) => (latestTime = Math.max(latestTime, timestamp)));

    return latestTime;
}

function getLabels(fenceStart: number, fenceEnd: number, period: chartPeriods, dataPoints: ChartData): LabelObjects {
    const result: { label: string; size: number }[] = [];
    let stepper = stepHour;
    let labelFormatter: any = formatTime;

    const d = new Date(fenceStart);

    if (period !== chartPeriods.INTRADAY) {
        snapToDay(d);
    }

    switch (period) {
        case chartPeriods.ONE_MONTH:
            snapToWeek(d);
            labelFormatter = formatDate;
            stepper = stepWeek;
            break;
        case chartPeriods.ONE_YEAR:
            snapToQuarter(d);
            labelFormatter = formatQuarter;
            stepper = stepQuarter;
            break;
        case chartPeriods.THREE_YEARS:
        case chartPeriods.FIVE_YEARS:
        case chartPeriods.MAX:
            snapToYear(d);
            labelFormatter = formatYear;
            stepper = stepYear;
    }

    while (d.getTime() < fenceEnd) {
        const obj = {
            label: labelFormatter(d),
            size: 0
        };
        const lastStep = d.getTime();
        stepper(d);

        obj.size =
            period !== chartPeriods.ONE_MONTH
                ? getSizeByTime(fenceStart, fenceEnd, lastStep, d.getTime())
                : getSizeByIndex(dataPoints, lastStep, d.getTime());

        result.push(obj);
    }

    if (period !== chartPeriods.INTRADAY && period !== chartPeriods.ONE_MONTH) {
        result[0].label = "";
    }

    return result;
}

export const TimeAxis = ({ fenceStart, fenceEnd, period, dataPoints }: TimeAxisProps) => {
    const labels = getLabels(fenceStart, fenceEnd, period, dataPoints);
    const isCondensed = labels.length > 14 && period === chartPeriods.INTRADAY ? "condensed" : undefined;
    return (
        <div className={`timeAxis${isCondensed ? " condensed" : ""}`}>
            {labels.map(({ label, size }) => (
                <span key={label} style={{ width: `${size}%` }}>
                    {label}
                </span>
            ))}
        </div>
    );
};

const style = { width: "50%" };
export const CondensedTimeAxis = ({ dataPoints }: TimeAxisProps) => {
    return (
        <div className="timeAxis">
            <span style={style}>{formatTime(getTimeOfEarliestDataPoint(dataPoints))}</span>
            <span style={style}>{formatTime(getTimeOfLatestDataPoint(dataPoints))}</span>
        </div>
    );
};
